<script setup>
/**
 * Site Terms Modal (תקנון האתר)
 * Asks client users to confirm they read the site terms and are over 18.
 * Soft ask: dismissible (X / click outside), never blocks the catalog, shown once per
 * catalog load until `siteTerms.accepted === true`. Accepting writes the nested
 * `siteTerms` object (see src/core/utils/consent.js). No versioning — accepted once is final.
 */
import { ref } from 'vue'
import { RouterLink } from 'vue-router'
import { useAuthStore } from '@app/store/auth.js'
import { updateDocument } from '@core/firebase/firestore.js'
import { buildSiteTermsPayload } from '@core/utils/consent.js'
import { CONSENT_SOURCE } from '@core/config/consentConstants.js'
import { ROUTES } from '@core/config/constants.js'

defineProps({
  open: { type: Boolean, default: false },
})

const emit = defineEmits(['close'])

const authStore = useAuthStore()
const saving = ref(false)

async function accept() {
  if (saving.value) return
  saving.value = true
  try {
    const clientMakat = authStore.user.client?.makat
    const uid = authStore.userId
    if (clientMakat) {
      const siteTerms = buildSiteTermsPayload({ uid, source: CONSENT_SOURCE.APP })
      await updateDocument('ClientsV2', clientMakat, { siteTerms })
      // Update local client object so the modal doesn't re-trigger
      authStore.user.client.siteTerms = { ...siteTerms, acceptedAt: new Date() }
    }
  } catch (err) {
    console.error('[SiteTermsModal] Failed to record site terms acceptance:', err)
  } finally {
    saving.value = false
    emit('close')
  }
}

function dismiss() {
  emit('close')
}
</script>

<template>
  <Teleport to="body">
    <Transition name="terms-modal">
      <div v-if="open" class="terms-overlay" @click.self="dismiss">
        <div class="terms-modal" role="dialog" aria-labelledby="site-terms-title">
          <!-- Close button -->
          <button class="terms-close" @click="dismiss" :disabled="saving" aria-label="סגור">
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          <!-- Illustration: shield with check -->
          <div class="terms-illustration">
            <svg viewBox="0 0 120 120" width="120" height="120" fill="none">
              <circle cx="60" cy="60" r="56" fill="var(--bg-secondary)" />
              <circle cx="60" cy="60" r="48" fill="var(--bg-tertiary)" />
              <path
                d="M60 26l26 9v20c0 17-11 28-26 33-15-5-26-16-26-33V35z"
                fill="var(--color-primary)"
                opacity="0.12"
                stroke="var(--color-primary)"
                stroke-width="2"
                stroke-linejoin="round"
              />
              <path
                d="M48 60l8 8 16-16"
                stroke="var(--color-primary)"
                stroke-width="3"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <!-- Content -->
          <div class="terms-content">
            <h2 id="site-terms-title" class="terms-title">תקנון האתר</h2>
            <p class="terms-description">
              בעת השימוש באפליקציה הנכם מאשרים כי קראתם את תקנון האתר, וכי אתם בוגרים מעל לגיל 18.
            </p>
            <div class="terms-links">
              <RouterLink :to="{ name: ROUTES.POLICY }" target="_blank" class="terms-link">
                תקנון
              </RouterLink>
              <span class="terms-links-sep" aria-hidden="true">·</span>
              <RouterLink :to="{ name: ROUTES.PRIVACY }" target="_blank" class="terms-link">
                מדיניות פרטיות
              </RouterLink>
            </div>
          </div>

          <!-- Action -->
          <div class="terms-actions">
            <button class="terms-accept-btn" @click="accept" :disabled="saving">
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
              קראתי ואני מאשר/ת
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.terms-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: var(--spacing-md);
}

.terms-modal {
  position: relative;
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl) var(--spacing-lg) var(--spacing-lg);
  max-width: 420px;
  width: 100%;
  text-align: center;
  box-shadow: var(--shadow-xl);
}

.terms-close {
  position: absolute;
  top: var(--spacing-sm);
  left: var(--spacing-sm);
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: var(--border-radius);
  color: var(--text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.terms-close:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.terms-illustration {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--spacing-lg);
}

.terms-content {
  margin-bottom: var(--spacing-lg);
}

.terms-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-sm);
}

.terms-description {
  font-size: var(--font-size-base);
  line-height: 1.6;
  color: var(--text-secondary);
  margin: 0 0 var(--spacing-sm);
}

.terms-links {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  font-size: var(--font-size-sm);
}

.terms-link {
  color: var(--color-info);
  text-decoration: underline;
  text-underline-offset: 3px;
}

.terms-link:hover {
  color: var(--color-primary);
}

.terms-links-sep {
  color: var(--text-muted);
}

.terms-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.terms-accept-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-xl);
  background: var(--color-primary);
  color: var(--text-inverse);
  border: none;
  border-radius: var(--border-radius);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.terms-accept-btn:hover:not(:disabled) {
  background: var(--color-primary-hover);
}

.terms-accept-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.terms-modal-enter-active,
.terms-modal-leave-active {
  transition: opacity var(--transition-base);
}

.terms-modal-enter-active .terms-modal,
.terms-modal-leave-active .terms-modal {
  transition: transform var(--transition-base);
}

.terms-modal-enter-from,
.terms-modal-leave-to {
  opacity: 0;
}

.terms-modal-enter-from .terms-modal,
.terms-modal-leave-to .terms-modal {
  transform: translateY(12px);
}

@media (prefers-reduced-motion: reduce) {
  .terms-modal-enter-active,
  .terms-modal-leave-active,
  .terms-modal-enter-active .terms-modal,
  .terms-modal-leave-active .terms-modal {
    transition: none;
  }
}
</style>
